import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskLine, Reveal, ScaleImage } from "@/components/site/Reveal";
import ClientLogos from "@/components/site/ClientLogos";
import CountUp from "@/components/site/CountUp";
import Lightbox from "@/components/site/Lightbox";
import { COMPANY, WHY_CHOOSE, INDUSTRIES } from "@/data/content";

const HERO_IMG = "/assets/brand/hero_bg_wide.png";

const FEATURED = [
  { src: "/assets/images/marine/royalihc_winch_drums_7.jpg", title: "Winch Drums", client: "Royal IHC", span: "md:col-span-7" },
  { src: "/assets/images/marine/royalihc_dredger_pipe_2.jpg", title: "Dredger Pipe Sections", client: "Royal IHC", span: "md:col-span-5" },
  { src: "/assets/images/marine/ship_gantry_royal_ihc_2.webp", title: "Ship Gantry Structure", client: "Royal IHC", span: "md:col-span-5" },
  { src: "/assets/images/marine/dredger_loading_box_royalihc.jpg", title: "Dredger Loading Box", client: "Royal IHC", span: "md:col-span-7" },
  { src: "/assets/images/additional/submarine_hydroplane_mdl.png", title: "Submarine Hydroplane", client: "Mazagon Dock Limited", span: "md:col-span-7" },
  { src: "/assets/images/defence/truck_mounted_gun_tata_power_3.jpg", title: "Truck-Mounted Gun", client: "Tata Power", span: "md:col-span-5" },
  { src: "/assets/images/marine/royalihc_winch_drums_2.jpg", title: "Winch Drum Assembly", client: "Royal IHC", span: "md:col-span-5" },
  { src: "/assets/images/steel/withdrawal_straightening_unit_sms_india_1.jpg", title: "Withdrawal Straightening Unit", client: "SMS India", span: "md:col-span-7" },
  { src: "/assets/images/additional/additional5.jpeg", title: "Ship Gantry - Fixed Part", client: "Royal IHC", span: "md:col-span-5" },
];

export default function Home() {
  const heroRef = useRef(null);
  const [lbIndex, setLbIndex] = useState(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 md:pt-40 pb-0 overflow-hidden">
        {/* Translucent full-bleed background image */}
        <motion.img
          src={HERO_IMG}
          alt="Precision engineering workshop"
          style={{ y: imgY, scale: imgScale }}
          className="pointer-events-none absolute inset-0 top-20 h-[80%] w-full object-cover object-center opacity-[0.22]"
        />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_60%_at_50%_38%,transparent_0%,rgba(255,255,255,0.45)_58%,#ffffff_85%)]" />

        <div className="relative mx-auto max-w-[1500px] px-6 md:px-12">
          {/* Centered headline */}
          <div className="relative">
            <div className="relative flex flex-col items-center text-center py-10 md:py-14">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex items-center justify-center gap-3 mb-8"
              >
                <span className="h-1.5 w-1.5 bg-[hsl(var(--signal))] animate-pulse" />
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  where engineering meets excellence
                </span>
              </motion.div>

              <h1 className="font-display font-medium tracking-tight text-[2.5rem] sm:text-5xl lg:text-[4.75rem] leading-[0.95] text-center">
                <MaskLine delay={0.15}>Manufacturing Equipment</MaskLine>
                <MaskLine delay={0.28}>That Keeps</MaskLine>
                <MaskLine delay={0.41}>
                  Industries <span className="text-muted-foreground">Running</span>
                </MaskLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="mt-8 max-w-2xl mx-auto text-muted-foreground text-base lg:text-lg leading-relaxed"
              >
                {"A fabrication, machining and assembly partner for high-quality, critical components across defence, marine & offshore, energy and heavy engineering sectors — trusted since 1993"}
              </motion.p>
            </div>
          </div>

          {/* Stat row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-16 border border-border"
          >
            <div className="flex items-center justify-center gap-3 py-4 border-b border-border bg-secondary">
              <span className="h-1.5 w-1.5 bg-[hsl(var(--signal))]" />
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                Our Growth Story
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                ["30+", "Years of Legacy"],
                ["50K+", "Sq Ft — Integrated Manufacturing Facility"],
                ["2K+", "Orders Completed"],
                ["20+", "Clients"],
              ].map(([n, l]) => (
                <div key={l} className="flex flex-col items-center text-center px-6 py-10">
                  <CountUp target={n} className="font-display font-bold text-5xl md:text-7xl tracking-tight" />
                  <p className="font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ 01 — Industries We Serve ]
                </span>
                <h2 className="mt-4 font-display font-medium tracking-tight text-4xl md:text-5xl leading-[1.05] max-w-xl">
                  Infinite possibilities across industries.
                </h2>
              </div>
              <p className="text-muted-foreground text-sm max-w-xs md:text-right">
                From naval submarines to continuous casting lines — engineered to the standards each sector demands.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {["marine", "defence", "steel"]
              .map((id) => INDUSTRIES.find((x) => x.id === id))
              .filter(Boolean)
              .map((ind, i) => (
              <Reveal key={ind.id} delay={i * 0.08}>
                <Link to={`/industries#${ind.id}`} className="group block" data-testid={`industry-card-${ind.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono-tech text-sm text-[hsl(var(--signal))]">0{i + 1}</span>
                    <h3 className="font-display font-medium text-2xl md:text-3xl tracking-tight">{ind.name}</h3>
                  </div>
                  <div className="relative overflow-hidden">
                    <img
                      src={ind.image}
                      alt={ind.name}
                      loading="lazy"
                      className="img-frame h-[240px] md:h-[280px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-5 text-muted-foreground text-sm leading-relaxed">{ind.blurb}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="py-16 md:py-24 mt-8 md:mt-12">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ 02 — Trusted By ]
                </span>
                <h2 className="mt-4 font-display font-medium tracking-tight text-4xl md:text-5xl leading-[1.05] max-w-xl">
                  Chosen by leaders in defence, marine & heavy engineering.
                </h2>
              </div>
              <p className="text-muted-foreground text-sm max-w-xs md:text-right">
                Partnering on India&apos;s most demanding programmes — and a growing international client base.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <ClientLogos />
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-24 bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-white/40">
              [ 03 — Why Krystal ]
            </span>
            <h2 className="mt-4 mb-16 font-display font-medium tracking-tight text-4xl md:text-6xl leading-[1] max-w-3xl">
              Engineered for tolerance. Built for trust.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
            {WHY_CHOOSE.map((item, i) => (
              <Reveal key={item.no} delay={(i % 3) * 0.08}>
                <div className="group h-full border-r border-b border-white/10 p-8 md:p-10 hover:bg-white/[0.03] transition-colors">
                  <p className="font-mono-tech text-sm text-[hsl(var(--signal))] mb-8">{item.no}</p>
                  <h3 className="font-display font-medium text-xl lg:text-2xl tracking-tight mb-4">{item.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                  [ 04 — Work Highlights ]
                </span>
                <h2 className="mt-4 font-display font-medium tracking-tight text-4xl md:text-5xl leading-[1.05] max-w-xl">
                  Components at the heart of critical programmes.
                </h2>
              </div>
              <Link to="/industries" className="group inline-flex items-center gap-2 font-mono-tech text-[12px] uppercase tracking-widest">
                Full portfolio
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {FEATURED.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.1}>
                <button
                  type="button"
                  onClick={() => setLbIndex(i)}
                  data-testid={`highlight-image-${i}`}
                  aria-label={`View ${f.title}`}
                  className="group relative block w-full text-left cursor-pointer"
                >
                  <ScaleImage src={f.src} alt={f.title} className="h-[280px] md:h-[340px]" watermark={true} />
                  <span className="absolute top-4 right-4 z-30 grid place-items-center h-9 w-9 text-white bg-black/50 backdrop-blur border border-white/40 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                  <div className="pointer-events-none absolute bottom-0 right-0 w-2/3 h-24 bg-gradient-to-l from-black/70 to-transparent z-10" />
                  <div className="absolute bottom-4 right-5 z-20 text-right max-w-[70%]">
                    <p className="font-mono-tech text-[10px] uppercase tracking-widest text-white/80 mb-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">{f.client}</p>
                    <h3 className="font-display font-medium text-xl text-white tracking-tight leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">{f.title}</h3>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        open={lbIndex !== null}
        images={FEATURED.map((f) => f.src)}
        index={lbIndex ?? 0}
        title={lbIndex !== null ? FEATURED[lbIndex].title : ""}
        client={lbIndex !== null ? FEATURED[lbIndex].client : ""}
        onClose={() => setLbIndex(null)}
        onIndexChange={setLbIndex}
      />
    </div>
  );
}
